/**
 * Starting layouts of a new chart (W2-14 onboarding-wizard; DESIGN.md §4.3 "(5) START", §5.1 step 6). Every layout is
 * built only through Core command bodies inside the creating transaction:
 *  - One line: line *Main* at (0, -250), opacity 100 %, rotation 0, scroll speed 10, each one keyframe at beat 0,
 *    "Hide notes under the line" on (the 'standard' line preset);
 *  - Two lines: *Main* at (0, -250) and a second line at (0, 150), so notes can arrive on two heights;
 *  - Empty: no line (the user adds them from the Lines panel);
 *  - RPE-style 24 lines (Full level): line 0 visible at (0, -250), lines 1 - 23 at (0, 0) with opacity 0 (RPE's usual
 *    template; they appear as dashed outlines in the preview's Edit mode).
 */

import type { NewChartDraft } from '../../app/registry/types';
import { Core } from '../../core/commands';
import type { KeyId, LineId } from '../../core/model/types';
import type { TxBuilder } from '../../core/ops/types';
import type { ExperienceLevel } from '../../state/types';

export type StartLayout = NewChartDraft['start'];

export interface StartLayoutInfo {
  readonly id: StartLayout;
  /** Level at which the layout is offered. */
  readonly level: ExperienceLevel;
  readonly lines: number;
}

export const START_LAYOUTS: readonly StartLayoutInfo[] = [
  { id: 'oneLine', level: 'guided', lines: 1 },
  { id: 'twoLines', level: 'guided', lines: 2 },
  { id: 'empty', level: 'guided', lines: 0 },
  { id: 'rpe24', level: 'full', lines: 24 },
];

/** Line names in the user's language. */
export interface StartLayoutNames {
  readonly main: string;
  readonly second: string;
  /** Name of line `index` of the RPE-style layout. */
  readonly numbered: (index: number) => string;
}

/** Position of the second line of the two-line layout (RPE px). */
export const SECOND_LINE_POSITION: readonly [number, number] = [0, 150];
/** Number of lines of the RPE-style layout. */
export const RPE_LAYOUT_LINES = 24;

/** The beat-0 opacity keyframe of a line (every preset line has one). */
function alphaKey(tx: TxBuilder, line: LineId): KeyId | undefined {
  const track = tx.idx.track(line, 'alpha', 0);
  return track === undefined ? undefined : tx.idx.keysByTrack(track)[0];
}

/** Adds the lines of `layout` (in export order) and returns their ids. */
export function applyStartLayout(tx: TxBuilder, layout: StartLayout, names: StartLayoutNames): LineId[] {
  switch (layout) {
    case 'oneLine':
      return [Core.lines.addLine(tx, { preset: 'standard', name: names.main })];
    case 'twoLines':
      return [
        Core.lines.addLine(tx, { preset: 'standard', name: names.main }),
        Core.lines.addLine(tx, { preset: 'standard', name: names.second, at: SECOND_LINE_POSITION }),
      ];
    case 'empty':
      return [];
    case 'rpe24': {
      const ids: LineId[] = [];
      for (let i = 0; i < RPE_LAYOUT_LINES; i++) {
        const id = Core.lines.addLine(tx, {
          preset: 'standard',
          name: names.numbered(i),
          ...(i === 0 ? {} : { at: [0, 0] as const }),
        });
        if (i > 0) {
          const key = alphaKey(tx, id);
          if (key !== undefined) Core.keys.setKeyValues(tx, { keys: [key], part: 'both', value: 0 });
        }
        ids.push(id);
      }
      return ids;
    }
  }
}
